import { ZoneFormCache, ZoneFormComponentProps, getLatestZone } from '../types'
import { Button } from 'components/Button/Button'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import SwitchStyled from 'pages/Robot/RobotView/RobotLeftBlock/RobotControl/SwitchStyled'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  selectMissionEditingZone,
  selectMissionInfo,
  selectMissionTempZone,
  selectSavingZoneProgress,
} from 'store/selectors/mission'
import store from 'store/store'
import { missionThunks } from 'store/thunks/mission'
import {
  DEFAULT_FRONT_DUST_SUPPRESSION,
  DEFAULT_SCRUBBER_BRUSH_SPEED,
  DEFAULT_TURBINE_SPEED,
  MISSION_ZONE_TYPES,
  PROGRESS_TYPES,
} from 'utils/constants'
import { ICleaningZone, RootState } from 'utils/interfaces'

import { resolveZoneSetup } from './utils'

import style from './ZoneDetails.module.css'

const createSpeedOptions = (start: number, end: number, step: number): IOption[] => {
  return Array.from({ length: Math.floor((end - start) / step) + 1 }, (_, i) => {
    const speed = (start + i * step).toFixed(1)
    return {
      text: speed,
      value: parseFloat(speed),
    }
  })
}

const cleaningSpeedOptions: IOption[] = createSpeedOptions(0.2, 1.5, 0.1)

const applyEquipmentDefaults = (
  setTurbineSpeed: (val: number) => void,
  setFrontDustSuppression: (val: boolean) => void,
  setScrubberBrushSpeed: (val: number) => void,
  setCacheValue?: (key: keyof ZoneFormCache, value: any) => void
) => {
  setTurbineSpeed(DEFAULT_TURBINE_SPEED)
  setFrontDustSuppression(DEFAULT_FRONT_DUST_SUPPRESSION)
  setScrubberBrushSpeed(DEFAULT_SCRUBBER_BRUSH_SPEED)
  setCacheValue?.('turbineSpeed', DEFAULT_TURBINE_SPEED)
  setCacheValue?.('frontDustSuppression', DEFAULT_FRONT_DUST_SUPPRESSION)
  setCacheValue?.('scrubberBrushSpeed', DEFAULT_SCRUBBER_BRUSH_SPEED)
}

const ZoneCleaningSettings = ({
  onSaveClicked,
  zone,
  getCacheValue,
  setCacheValue,
  getAllCacheValues,
}: ZoneFormComponentProps) => {
  const { t } = useTranslation()
  const editingZone = useSelector(selectMissionEditingZone)
  const tempZone = useSelector(selectMissionTempZone)
  const savingProgress = useSelector(selectSavingZoneProgress)
  const cleaningZones = useSelector((state: RootState) => state.mission.cleaningZones)
  const missionInfo = useSelector(selectMissionInfo)
  const isSweeper = missionInfo?.robot_type === 'Sweeper'

  const zoneToUse = editingZone || zone
  const latestZone = getLatestZone(cleaningZones, zoneToUse?.properties?.zone_id)
  const finalZone = latestZone || zoneToUse

  const getInitialValue = <K extends keyof ZoneFormCache>(key: K, defaultValue: ZoneFormCache[K]): ZoneFormCache[K] => {
    const cached = getCacheValue?.(key)
    if (cached !== undefined && cached !== null) {
      return cached
    }
    if ((key === 'turbineSpeed' || key === 'frontDustSuppression' || key === 'scrubberBrushSpeed') && finalZone) {
      const equipmentSetup = (finalZone as ICleaningZone)?.properties?.equipment_setup
      if (equipmentSetup) {
        if (key === 'turbineSpeed' && equipmentSetup.turbine_speed !== undefined) {
          return equipmentSetup.turbine_speed as ZoneFormCache[K]
        }
        if (key === 'frontDustSuppression' && equipmentSetup.front_dust_suppression !== undefined) {
          return equipmentSetup.front_dust_suppression as ZoneFormCache[K]
        }
        if (key === 'scrubberBrushSpeed' && equipmentSetup.scrubber_brush_speed !== undefined) {
          return equipmentSetup.scrubber_brush_speed as ZoneFormCache[K]
        }
      }
    }
    return defaultValue
  }

  const [turbineSpeed, setTurbineSpeed] = useState<number>(() => {
    return (getInitialValue('turbineSpeed', DEFAULT_TURBINE_SPEED) as number) || DEFAULT_TURBINE_SPEED
  })
  const [frontDustSuppression, setFrontDustSuppression] = useState<boolean>(() => {
    return (
      (getInitialValue('frontDustSuppression', DEFAULT_FRONT_DUST_SUPPRESSION) as boolean) ??
      DEFAULT_FRONT_DUST_SUPPRESSION
    )
  })
  const [scrubberBrushSpeed, setScrubberBrushSpeed] = useState<number>(() => {
    return (
      (getInitialValue('scrubberBrushSpeed', DEFAULT_SCRUBBER_BRUSH_SPEED) as number) || DEFAULT_SCRUBBER_BRUSH_SPEED
    )
  })

  const [cleaningSpeed, setCleaningSpeed] = useState<number>(() => {
    return (
      (getInitialValue(
        'cleaningSpeed',
        (zone as ICleaningZone)?.properties?.cleaning_speed || missionInfo?.cleaning_speed || 0.5
      ) as number) || 0.5
    )
  })
  const [transportSpeed, setTransportSpeed] = useState<number>(() => {
    return (
      (getInitialValue(
        'transportSpeed',
        (zone as ICleaningZone)?.properties?.transport_speed || missionInfo?.transport_speed || 0.5
      ) as number) || 0.5
    )
  })
  const [isBorderCleaning, setIsBorderCleaning] = useState<boolean>(() => {
    return (
      (getInitialValue(
        'isBorderCleaning',
        (zone as ICleaningZone)?.properties?.is_border_cleaning || false
      ) as boolean) || false
    )
  })

  const cleaningSpeedOption = cleaningSpeedOptions.find(opt => opt.value === cleaningSpeed) || {
    text: cleaningSpeed.toString(),
    value: cleaningSpeed,
  }

  useEffect(() => {
    setCacheValue?.('turbineSpeed', turbineSpeed)
    setCacheValue?.('frontDustSuppression', frontDustSuppression)
    setCacheValue?.('scrubberBrushSpeed', scrubberBrushSpeed)
    setCacheValue?.('cleaningSpeed', cleaningSpeed)
    setCacheValue?.('transportSpeed', transportSpeed)
    setCacheValue?.('isBorderCleaning', isBorderCleaning)
  }, [
    turbineSpeed,
    frontDustSuppression,
    scrubberBrushSpeed,
    cleaningSpeed,
    transportSpeed,
    isBorderCleaning,
    setCacheValue,
  ])

  const zoneIdRef = useRef<number | undefined>(zoneToUse?.properties?.zone_id)

  useEffect(() => {
    const currentZoneId = zoneToUse?.properties?.zone_id
    if (zoneIdRef.current !== currentZoneId) {
      zoneIdRef.current = currentZoneId

      const equipmentSetup = finalZone ? (finalZone as ICleaningZone)?.properties?.equipment_setup : null
      if (equipmentSetup) {
        const turbineSpeedValue =
          equipmentSetup.turbine_speed !== undefined && equipmentSetup.turbine_speed !== null
            ? equipmentSetup.turbine_speed
            : DEFAULT_TURBINE_SPEED
        const frontDustSuppressionValue =
          equipmentSetup.front_dust_suppression !== undefined && equipmentSetup.front_dust_suppression !== null
            ? equipmentSetup.front_dust_suppression
            : DEFAULT_FRONT_DUST_SUPPRESSION
        const scrubberBrushSpeedValue =
          equipmentSetup.scrubber_brush_speed !== undefined && equipmentSetup.scrubber_brush_speed !== null
            ? equipmentSetup.scrubber_brush_speed
            : DEFAULT_SCRUBBER_BRUSH_SPEED

        setTurbineSpeed(turbineSpeedValue)
        setFrontDustSuppression(frontDustSuppressionValue)
        setScrubberBrushSpeed(scrubberBrushSpeedValue)
        setCacheValue?.('turbineSpeed', turbineSpeedValue)
        setCacheValue?.('frontDustSuppression', frontDustSuppressionValue)
        setCacheValue?.('scrubberBrushSpeed', scrubberBrushSpeedValue)
      } else {
        applyEquipmentDefaults(setTurbineSpeed, setFrontDustSuppression, setScrubberBrushSpeed, setCacheValue)
      }

      const properties = (finalZone as ICleaningZone)?.properties
      const newCleaningSpeed = properties?.cleaning_speed || missionInfo?.cleaning_speed || 0.5
      const newTransportSpeed = properties?.transport_speed || missionInfo?.transport_speed || 0.5
      const newIsBorderCleaning = properties?.is_border_cleaning || false

      setCleaningSpeed(newCleaningSpeed)
      setTransportSpeed(newTransportSpeed)
      setIsBorderCleaning(newIsBorderCleaning)
      setCacheValue?.('cleaningSpeed', newCleaningSpeed)
      setCacheValue?.('transportSpeed', newTransportSpeed)
      setCacheValue?.('isBorderCleaning', newIsBorderCleaning)
    }
  }, [zoneToUse?.properties?.zone_id, finalZone, setCacheValue])

  const savingProgressInWork = useMemo(() => {
    return savingProgress === PROGRESS_TYPES.WORK
  }, [savingProgress])

  const onTurbineSpeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    if (inputValue === '') {
      setTurbineSpeed(DEFAULT_TURBINE_SPEED)
      setCacheValue?.('turbineSpeed', DEFAULT_TURBINE_SPEED)
      return
    }
    const value = parseFloat(inputValue)
    if (!isNaN(value)) {
      setTurbineSpeed(value)
      setCacheValue?.('turbineSpeed', value)
    }
  }

  const onScrubberBrushSpeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    if (inputValue === '') {
      setScrubberBrushSpeed(DEFAULT_SCRUBBER_BRUSH_SPEED)
      setCacheValue?.('scrubberBrushSpeed', DEFAULT_SCRUBBER_BRUSH_SPEED)
      return
    }
    const value = parseFloat(inputValue)
    if (!isNaN(value)) {
      setScrubberBrushSpeed(value)
      setCacheValue?.('scrubberBrushSpeed', value)
    }
  }

  const onCleaningSpeedChange = (speed: number) => {
    setCleaningSpeed(speed)
    setCacheValue?.('cleaningSpeed', speed)
  }

  const onTransportSpeedChange = (speed: number) => {
    setTransportSpeed(speed)
    setCacheValue?.('transportSpeed', speed)
  }

  const onBorderCleaningChanged = (checked: boolean) => {
    setIsBorderCleaning(checked)
    setCacheValue?.('isBorderCleaning', checked)
  }

  const getZoneFieldsFromCacheOrZone = () => {
    const cache = getAllCacheValues?.()
    const fallbackZone = tempZone || editingZone || zone
    const properties = (fallbackZone as ICleaningZone)?.properties

    return {
      zoneName: cache?.name || properties?.name || 'New Zone',
      zoneDescription: cache?.description || properties?.description || '',
      zoneType: (cache?.selectedZoneType || properties?.zone_type || MISSION_ZONE_TYPES.CLEANING) as MISSION_ZONE_TYPES,
      cleaningSpeed:
        cache?.cleaningSpeed !== undefined
          ? cache.cleaningSpeed
          : properties?.cleaning_speed ?? missionInfo?.cleaning_speed,
      transportSpeed:
        cache?.transportSpeed !== undefined
          ? cache.transportSpeed
          : properties?.transport_speed ?? missionInfo?.transport_speed,
      isBorderCleaning:
        cache?.isBorderCleaning !== undefined ? cache.isBorderCleaning : properties?.is_border_cleaning ?? false,
    }
  }

  const onSave = (keepEditing?: boolean) => {
    const cache = getAllCacheValues?.()
    const equipment_setup = {
      turbine_speed: turbineSpeed,
      front_dust_suppression: frontDustSuppression,
      scrubber_brush_speed: scrubberBrushSpeed,
    }
    const zoneToUse = tempZone || editingZone || zone
    const properties = (zoneToUse as ICleaningZone)?.properties
    const setup = resolveZoneSetup(cache, zoneToUse)
    const zoneFields = getZoneFieldsFromCacheOrZone()

    return onSaveClicked({
      ...zoneFields,
      equipment_setup,
      setup,
      keepEditing,
    })
  }

  const onRecalculate = async () => {
    try {
      await onSave(true)
      if (zoneToUse?.properties?.zone_id) {
        await store.dispatch(missionThunks.startCalculatingRoute(zoneToUse.properties.zone_id))
      }
    } catch (error) {
      console.error('Failed to save or recalculate route:', error)
    }
  }

  const onCancelClicked = () => {
    store.dispatch(missionThunks.cancelEditingZone())
  }

  const hasTurbineSpeedError = turbineSpeed < 300 || turbineSpeed > 3000
  const hasScrubberBrushSpeedError = scrubberBrushSpeed < 100 || scrubberBrushSpeed > 2000

  return (
    <div className={style.root}>
      <div className={style.content}>
        <div style={{ marginTop: '8px', marginBottom: '16px' }}>
          <Dropdown
            noShadow
            options={cleaningSpeedOptions}
            currentOption={cleaningSpeedOption}
            onClickOption={opt => {
              const val = Number(opt.value)
              onCleaningSpeedChange(val)
            }}
            description={t('mission_settings.cleaning_speed')}
          />
          <div style={{ fontSize: '12px', color: 'gray', marginTop: '4px', paddingLeft: '14px' }}>
            {t('zone_details.cleaning_speed_desc') || 'Robot speed during cleaning operations'}
          </div>
        </div>
        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
          <TextFieldDescribed
            className={style.equipmentTextfield}
            description="Turbine speed (RPM, 300-3000)"
            placeholder={DEFAULT_TURBINE_SPEED.toString()}
            type="number"
            value={turbineSpeed.toString()}
            onChange={onTurbineSpeedChange}
            hasError={hasTurbineSpeedError}
            error="Turbine speed must be between 300 and 3000 RPM"
          />
          <div style={{ fontSize: '12px', color: 'gray', marginTop: '4px', paddingLeft: '14px' }}>
            {t('zone_details.turbine_speed_desc') || 'RPM speed of the suction turbine'}
          </div>
        </div>
        {!isSweeper && (
          <div style={{ marginTop: '16px', marginBottom: '16px' }}>
            <TextFieldDescribed
              className={style.equipmentTextfield}
              description="Scrubber brush speed (RPM, 100-2000)"
              placeholder={DEFAULT_SCRUBBER_BRUSH_SPEED.toString()}
              type="number"
              value={scrubberBrushSpeed.toString()}
              onChange={onScrubberBrushSpeedChange}
              hasError={hasScrubberBrushSpeedError}
              error="Scrubber brush speed must be between 100 and 2000 RPM"
            />
            <div style={{ fontSize: '12px', color: 'gray', marginTop: '4px', paddingLeft: '14px' }}>
              {t('zone_details.scrubber_brush_speed_desc') || 'RPM speed of the scrubber brush'}
            </div>
          </div>
        )}
        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
          <div className={style.tableLockRow} style={{ margin: 0 }}>
            <div className={style.textAlignLeft}>Front dust suppression</div>
            <div className={style.alignRight}>
              <SwitchStyled
                checked={frontDustSuppression}
                onChange={e => {
                  setFrontDustSuppression(e.target.checked)
                  setCacheValue?.('frontDustSuppression', e.target.checked)
                }}
                disabled={false}
              />
            </div>
          </div>
          <div style={{ fontSize: '12px', color: 'gray', marginTop: '-4px', paddingLeft: '14px' }}>
            {t('zone_details.front_dust_suppression_desc') ||
              'Suppress dust by spraying water in front of the robot'}
          </div>
        </div>
        <div style={{ marginTop: '16px', marginBottom: '16px' }}>
          <div className={style.tableLockRow} style={{ margin: 0 }}>
            <div className={style.textAlignLeft}>Border cleaning</div>
            <div className={style.alignRight}>
              <SwitchStyled
                checked={isBorderCleaning}
                onChange={e => onBorderCleaningChanged(e.target.checked)}
                disabled={false}
              />
            </div>
          </div>
          <div style={{ fontSize: '12px', color: 'gray', marginTop: '-4px', paddingLeft: '14px' }}>
            {t('zone_details.border_cleaning_desc') || 'Clean the outer borders of the zone first'}
          </div>
        </div>
      </div>
      <div className={style.buttons} style={{ justifyContent: 'flex-end' }}>
        <div style={{ display: 'flex' }}>
          <Button.Outlined className={style.button} onClick={onCancelClicked} disabled={savingProgressInWork}>
            {t('common.cancel')}
          </Button.Outlined>
          <Button.Standart
            className={style.button}
            onClick={() => onSave()}
            disabled={hasTurbineSpeedError || (!isSweeper && hasScrubberBrushSpeedError) || savingProgressInWork}
            loading={savingProgressInWork}
          >
            {t('common.save')}
          </Button.Standart>
        </div>
      </div>
    </div>
  )
}

export default ZoneCleaningSettings
